import React from "react";
import { Formik, Form } from "formik";
import styled from "styled-components";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import Dialog from "common/basicComponents/Dialog";
import Typography, {
  TypographyVariant,
  TypographyWeight,
} from "common/basicComponents/Typography";
import Button, {
  ButtonVariant,
  ButtonType,
} from "common/basicComponents/Button";
import Box from "common/basicComponents/Box";
import FormInputField, {
  FormInputFieldType,
  NumberType,
} from "common/formFields/FormInputField";
import { useViewport, ViewportType } from "utils/ViewportContext";
import {
  addNotification,
  NotificationVariant,
} from "components/notifications/NotificationSlice";
import { updateOneTimeCharge } from "components/billing/BillingActions";

const Buttons = styled.div`
  display: flex;
  flex-direction: ${({ viewport }) =>
    viewport > ViewportType.MOBILE ? "row" : "column-reverse"};
  margin-top: 2.5rem;
  width: 100%;
`;

const EditOneTimeChargeDialog = ({ open, charge, onClose, onSaved }) => {
  const { t } = useTranslation("billing");
  const { type: viewportType } = useViewport();
  const dispatch = useDispatch();

  const onSubmit = async (values, form) => {
    const amount = Number(values.amount);
    if (!amount || amount <= 0) {
      form.setFieldError("amount", "common:validator.required");
      return;
    }
    form.setSubmitting(true);
    const ok = await updateOneTimeCharge(charge.id, {
      amount_minor: Math.round(amount * 100),
      description: values.description || undefined,
    });
    form.setSubmitting(false);
    if (ok) {
      dispatch(
        addNotification({
          variant: NotificationVariant.SUCCESS,
          message: t("oneTimeChargeUpdated"),
        })
      );
      onSaved();
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <Box padding="0 0 2.5rem">
        <Typography
          variant={TypographyVariant.H4}
          weight={TypographyWeight.BOLD}
          text={t("oneTimeCharge")}
        />
      </Box>
      <Formik
        initialValues={{
          amount: charge ? charge.amount / 100 : 0,
          description: charge?.description || "",
        }}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({ isSubmitting }) => (
          <Form>
            <Box width="100%">
              <FormInputField
                type={FormInputFieldType.NUMBER}
                numberCheck={NumberType.NON_NEGATIVE_DECIMAL}
                name="amount"
                id="amount"
                label={t("oneTimeAmount")}
                disabled={isSubmitting}
              />
            </Box>
            <Box width="100%">
              <FormInputField
                type={FormInputFieldType.TEXT}
                name="description"
                id="description"
                label={t("oneTimeDescription")}
                disabled={isSubmitting}
              />
            </Box>
            <Buttons viewport={viewportType}>
              <Box
                width={viewportType < ViewportType.TABLET ? "100%" : "50%"}
                padding={
                  viewportType < ViewportType.TABLET
                    ? "0.625rem 0"
                    : "0 1rem 0 0"
                }
              >
                <Button
                  variant={ButtonVariant.OUTLINED}
                  text={t("common:button.cancel")}
                  minWidth="13rem"
                  width="100%"
                  onClick={onClose}
                  disabled={isSubmitting}
                />
              </Box>
              <Box
                width={viewportType < ViewportType.TABLET ? "100%" : "50%"}
                padding={
                  viewportType < ViewportType.TABLET
                    ? "0.625rem 0"
                    : "0 0 0 1rem"
                }
              >
                <Button
                  variant={ButtonVariant.GREEN}
                  text={t("common:button.save")}
                  minWidth="13rem"
                  width="100%"
                  type={ButtonType.SUBMIT}
                  isLoading={isSubmitting}
                />
              </Box>
            </Buttons>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

EditOneTimeChargeDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  charge: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

EditOneTimeChargeDialog.defaultProps = {
  charge: null,
};

export default EditOneTimeChargeDialog;
